import React, { createContext, useContext, useEffect, useState, useMemo, useRef } from "react";
import { UserContextType, UserInfo, TenantInfo } from "./Interface";

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUserContext = () => {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error("useUserContext must be used inside UserDetailsProviderContext");
  return ctx;
};

const EMPTY_USER: UserContextType["user"] = {
  userId: null,
  objectId: null,
  email: null,
  isGlobalUser: false,
  tenant: null,
  isLoading: true,
  accessToken: null,
  permissions: [],
};

const SESSION_KEYS = [
  "userContext",
  "email",
  "tenantCode",
  "token",
  "objectId",
  "accessToken",
] as const;

const clearSessionStorageKeys = () => {
  SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
};

export const UserDetailsProviderContext = ({ children }) => {
  const [user, setUserState] = useState<UserContextType["user"]>(EMPTY_USER);
  const [tenant, setTenantState] = useState<TenantInfo | null>(null);
  const [userInfo, setUserInfoState] = useState<UserInfo | null>(null);
  // Prevent the persist effect from writing stale data back after logout/reset.
  const persistEnabledRef = useRef(true);

  useEffect(() => {
    const saved = localStorage.getItem("userContext");
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved);
      if (parsed?.user?.objectId && parsed?.user?.email) {
        setUserState({ ...EMPTY_USER, ...parsed.user });
        setTenantState(parsed.tenant ?? null);
        setUserInfoState(parsed.userInfo ?? null);
      }
    } catch {
      localStorage.removeItem("userContext");
    }
  }, []);

  useEffect(() => {
    if (!persistEnabledRef.current) return;

    localStorage.setItem(
      "userContext",
      JSON.stringify({ user, tenant, userInfo })
    );
    if (user?.email) localStorage.setItem("email", user.email);
    if (user?.tenant) localStorage.setItem("tenantCode", user.tenant);
  }, [user, tenant, userInfo]);

  const setUser = (data: Partial<UserContextType["user"]>) => {
    persistEnabledRef.current = true;
    if (data.email) localStorage.setItem("email", data.email);
    if (data.tenant) localStorage.setItem("tenantCode", data.tenant);
    if (data.objectId) localStorage.setItem("objectId", data.objectId);
    setUserState((prev) => ({ ...prev, ...data }));
  };

  /** Full replace — use on login bootstrap so previous account fields cannot linger. */
  const replaceUser = (data: UserContextType["user"]) => {
    persistEnabledRef.current = true;
    setUserState(data);
    if (data.email) localStorage.setItem("email", data.email);
    else localStorage.removeItem("email");
    if (data.tenant) localStorage.setItem("tenantCode", data.tenant);
    else localStorage.removeItem("tenantCode");
    if (data.objectId) localStorage.setItem("objectId", data.objectId);
    else localStorage.removeItem("objectId");
  };

  const setTenant = (data: Partial<TenantInfo>) =>
    setTenantState((prev) => ({ ...prev, ...data }));

  const replaceTenant = (data: TenantInfo | null) => setTenantState(data);

  const setUserInfo = (data: Partial<UserInfo>) =>
    setUserInfoState((prev) => ({ ...(prev || {}), ...data } as UserInfo));

  const replaceUserInfo = (data: UserInfo | null) => setUserInfoState(data);

  const setPermissions = (permissions: string[]) =>
    setUserState((prev) => ({ ...prev, permissions }));

  const resetUserContext = () => {
    persistEnabledRef.current = false;
    setUserState({ ...EMPTY_USER });
    setTenantState(null);
    setUserInfoState(null);
    clearSessionStorageKeys();
  };

  const value = useMemo(
    () => ({
      user,
      tenant,
      userInfo,
      setUser,
      replaceUser,
      setTenant,
      replaceTenant,
      setUserInfo,
      replaceUserInfo,
      setPermissions,
      resetUserContext,
    }),
    [user, tenant, userInfo]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
